import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ChevronRight, ChevronDown } from "lucide-react";
import {
  ujmsEventsTimeline, ujmsAddInternalEvent, ujmsCaseService, ujmsIssueService, ujmsReturnService,
  ujmsCauses, ujmsAddCause, ujmsDisposeCause, ujmsCaseDispositions,
} from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, btnPrimary, btnGhost, Field, fmtDT, fmtD, cls, can, EmptyState } from "../ui";
import api from "../../services/api";
import { FileText } from "lucide-react";
import { DispositionSheet } from "../enterprise/charges/ChargeDispositions";

const err = (e, f) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : f);

// ---------------- DETAIL ----------------
export function DetailTab({ c, meta, custody }) {
  const rows = [
    ["Case Number", c.case_number], ["Style / Caption", c.caption],
    ["Court", meta?.court_node || "—"], ["Category", meta?.category || "—"],
    ["Case Type", `${c.case_type_label || c.case_type}${c.case_subtype ? " — " + c.case_subtype : ""}`],
    ["Status", c.status], ["Confidentiality", c.confidentiality_level || "PUBLIC"],
    ["Division", c.division_name || "—"], ["Judicial Officer", c.current_judge_name || "Unassigned"],
    ["Courtroom", c.current_courtroom_name || "—"],
    ["Custody", custody?.in_custody ? "IN CUSTODY" : custody?.last_release ? `Released ${fmtD(custody.last_release.released_at)}` : "Not in custody"],
  ];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4" data-testid="tab-detail">
      <div className="xl:col-span-2">
        <Panel title="case detail" testId="detail-panel">
          <table className="w-full">
            <tbody>
              {rows.map(([k, v]) => (
                <tr key={k} className="border-b border-slate-100 last:border-0">
                  <td className="py-1.5 pr-4 text-[13px] text-slate-500 w-44">{k}</td>
                  <td className="py-1.5 text-[13px] font-semibold text-slate-800">{k === "Status" ? <Badge value={v} /> : v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      </div>
      <div className="space-y-4">
        <Panel title="filing information" testId="detail-filing-info">
          {[["Date Filed", fmtD(c.filing_date)], ["Filed By", c.created_by || "—"],
            ["Case Opened", fmtD(c.created_at)]].map(([k, v]) => (
            <div key={k} className="flex justify-between py-1 text-[13px] border-b border-slate-100 last:border-0">
              <span className="text-slate-500">{k}</span><span className="font-semibold text-slate-800">{v}</span>
            </div>
          ))}
        </Panel>
        <Panel title="flags" testId="detail-flags">
          <div className="flex flex-wrap gap-1.5">
            {(meta?.flags || []).map((f, i) => (
              <span key={i} title={f.tooltip} className="text-[11px] font-bold text-white px-2 py-0.5 rounded-sm cursor-help"
                style={{ background: f.color }} data-testid={`detail-flag-${i}`}>{f.label}</span>
            ))}
            {!(meta?.flags || []).length && <span className="text-xs text-slate-400">No flags on this case.</span>}
          </div>
        </Panel>
      </div>
    </div>
  );
}

// ---------------- EVENTS (docketable vs non-docketable) ----------------
export function EventsTab({ caseId }) {
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => { ujmsEventsTimeline(caseId).then((r) => setData(r.data)).catch(() => {}); }, [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-400 p-4">Loading events…</div>;
  const addNote = async () => {
    if (!note.trim()) return;
    try { await ujmsAddInternalEvent(caseId, note.trim()); toast.success("Non-docketable event entered"); setNote(""); load(); }
    catch (e) { err(e, "Failed"); }
  };
  return (
    <Panel title="events — case timeline" testId="tab-events"
      action={<span className="text-[11px] text-slate-500">
        <span className="inline-block w-2.5 h-2.5 bg-slate-900 rounded-[2px] mr-1 align-middle" />docketable (visible on the public Portal)
        <span className="inline-block w-2.5 h-2.5 bg-slate-300 rounded-[2px] ml-3 mr-1 align-middle" />non-docketable (court staff only)
      </span>}>
      {data.can_internal && (
        <div className="flex gap-2 mb-3 pb-3 border-b border-slate-100">
          <input className={inputCls} value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Add a non-docketable (internal) event — never visible on the public portal…" data-testid="events-internal-input" />
          <button className={btnPrimary} onClick={addNote} data-testid="events-internal-add">Add</button>
        </div>
      )}
      <table className="w-full border-collapse">
        <thead><tr><th className={thCls}>Date</th><th className={thCls}>Event</th><th className={thCls}>Description</th><th className={thCls}>Entered By</th><th className={thCls}>Visibility</th></tr></thead>
        <tbody>
          {data.events.map((e) => (
            <tr key={e.id} className={cls("hover:bg-slate-50", !e.docketable && "opacity-60")} data-testid={`event-row-${e.id}`}>
              <td className={cls(tdCls, "font-mono text-xs whitespace-nowrap", !e.docketable && "text-slate-400")}>{e.date} {e.time}</td>
              <td className={tdCls}>
                <span className={cls("text-[11px] font-bold px-1.5 py-0.5 rounded-sm",
                  e.docketable ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500")}>{e.event_code}</span>
              </td>
              <td className={cls(tdCls, "text-[13px]", e.docketable ? "text-slate-900 font-medium" : "text-slate-400 italic")}>{e.description}</td>
              <td className={cls(tdCls, "text-xs", !e.docketable && "text-slate-400")}>{e.entered_by}</td>
              <td className={tdCls}>{e.docketable
                ? <span className="text-[10px] font-bold text-emerald-700">PORTAL</span>
                : <span className="text-[10px] font-bold text-slate-400">INTERNAL</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Panel>
  );
}

// ---------------- SERVICE ----------------
const S_TONE = { SERVED: "bg-emerald-600", RETURNED_UNSERVED: "bg-red-600", ISSUED: "bg-amber-500" };
const S_TIP = { SERVED: "All service completed — served", RETURNED_UNSERVED: "Returned UNSERVED", ISSUED: "Issued — awaiting return of service" };

export function ServiceTab({ caseId, ws, access }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [form, setForm] = useState({ party_id: "", document_label: "Civil Summons" });
  const load = useCallback(() => { ujmsCaseService(caseId).then((r) => setData(r.data)).catch(() => {}); }, [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="text-sm text-slate-400 p-4">Loading service…</div>;
  const issue = async () => {
    if (!form.party_id) { toast.error("Select a party"); return; }
    try { await ujmsIssueService(caseId, form); toast.success("SUMMONS_ISSUED docketed"); load(); }
    catch (e) { err(e, "Failed"); }
  };
  const ret = async (rec, result) => {
    const method = result === "SERVED" ? (window.prompt("Service method (PERSONAL / SUBSTITUTE / CERTIFIED_MAIL / SHERIFF):", "PERSONAL") || "") : "";
    if (result === "SERVED" && method === "") return;
    const notes = window.prompt(result === "SERVED" ? "Notes (optional):" : "Reason returned unserved:", "") ?? "";
    try { await ujmsReturnService(rec.id, { result, method, notes }); toast.success(`SERVICE_RETURNED — ${result}`); load(); }
    catch (e) { err(e, "Failed"); }
  };
  return (
    <Panel title="service of process" testId="tab-service"
      action={can(access, "filing.accept", "case.create") && (
        <div className="flex gap-2 items-center">
          <select className={cls(selectCls, "w-44")} value={form.party_id} onChange={(e) => setForm({ ...form, party_id: e.target.value })} data-testid="service-party">
            <option value="">Party…</option>
            {(ws.parties || []).map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
          </select>
          <select className={cls(selectCls, "w-48")} value={form.document_label} onChange={(e) => setForm({ ...form, document_label: e.target.value })} data-testid="service-doc">
            {(data.documents || []).map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <button className={btnPrimary} onClick={issue} disabled={!(ws.parties || []).length}
            title={!(ws.parties || []).length ? "Add a party to this case before issuing service" : undefined}
            data-testid="service-issue-btn">Issue</button>
        </div>
      )}>
      {!data.records.length ? (
        <EmptyState icon={FileText} title="No service records"
          note="Issue a summons above — returns of service are tracked here with red/green served indicators." testId="service-empty" />
      ) : (
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls} style={{ width: 28 }}></th><th className={thCls}>S</th><th className={thCls}>Document</th><th className={thCls}>Party</th><th className={thCls}>Issued</th><th className={thCls}>Status</th><th className={thCls}></th></tr></thead>
          <tbody>
            {data.records.map((r) => (
              <React.Fragment key={r.id}>
                <tr className="hover:bg-slate-50" data-testid={`service-row-${r.id}`}>
                  <td className={tdCls}>
                    <button onClick={() => setExpanded((x) => ({ ...x, [r.id]: !x[r.id] }))} data-testid={`service-expand-${r.id}`} aria-label="Expand service record">
                      {expanded[r.id] ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                    </button>
                  </td>
                  <td className={tdCls}>
                    <span title={S_TIP[r.status]} data-testid={`service-indicator-${r.id}`}
                      className={cls("inline-flex items-center justify-center w-5 h-5 rounded-sm text-white text-[11px] font-black cursor-help", S_TONE[r.status])}>S</span>
                  </td>
                  <td className={cls(tdCls, "font-medium")}>{r.document_label}</td>
                  <td className={tdCls}>{r.party_name}</td>
                  <td className={cls(tdCls, "font-mono text-xs")}>{r.issued_date}</td>
                  <td className={tdCls}><Badge value={r.status} /></td>
                  <td className={tdCls}>
                    {r.status === "ISSUED" && can(access, "filing.accept", "warrant.serve", "custody.manage") && (
                      <div className="flex gap-1">
                        <button className={cls(btnGhost, "px-2 py-0.5 text-xs text-emerald-700")} onClick={() => ret(r, "SERVED")} data-testid={`service-served-${r.id}`}>Served</button>
                        <button className={cls(btnGhost, "px-2 py-0.5 text-xs text-red-700")} onClick={() => ret(r, "UNSERVED")} data-testid={`service-unserved-${r.id}`}>Unserved</button>
                      </div>
                    )}
                  </td>
                </tr>
                {expanded[r.id] && (
                  <tr className="bg-[#f2f7fc]" data-testid={`service-detail-${r.id}`}>
                    <td className={tdCls}></td>
                    <td className={tdCls} colSpan={6}>
                      <div className="text-xs text-slate-600 space-y-0.5 py-1">
                        <div><b>Issued by:</b> {r.issued_by} on {r.issued_date}</div>
                        {r.returned_at && <div><b>Returned:</b> {fmtDT(r.returned_at)} by {r.returned_by}</div>}
                        {r.served_at && <div><b>Served:</b> {fmtDT(r.served_at)} {r.service_method ? `(${r.service_method})` : ""}</div>}
                        {r.return_notes && <div><b>Notes:</b> {r.return_notes}</div>}
                        {!r.returned_at && <div className="text-amber-700">Awaiting return of service from the serving officer.</div>}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

// ---------------- CAUSES ----------------
const CAUSE_SUGGESTIONS = ["Breach of Contract", "Negligence — Motor Vehicle", "Claim of Lien", "Small Claim",
  "Custody", "Child Support", "Divorce — Absolute", "Domestic Violence Protective Order",
  "Guardianship", "Estate Administration", "Involuntary Commitment"];

export function CausesTab({ caseId, ws, access }) {
  const [causes, setCauses] = useState(null);
  const [form, setForm] = useState({ cause_type: "", filed_by_party_id: "", description: "" });
  const load = useCallback(() => { ujmsCauses(caseId).then((r) => setCauses(r.data)).catch(() => {}); }, [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!causes) return <div className="text-sm text-slate-400 p-4">Loading causes…</div>;
  const add = async () => {
    if (!form.cause_type.trim()) { toast.error("Cause type is required"); return; }
    try { await ujmsAddCause(caseId, form); toast.success("Cause of action added"); setForm({ cause_type: "", filed_by_party_id: "", description: "" }); load(); }
    catch (e) { err(e, "Failed"); }
  };
  const dispose = async (cz) => {
    const d = window.prompt("Disposition of this cause (e.g., Voluntary Dismissal, Judgment for Plaintiff):", "");
    if (!d) return;
    try { await ujmsDisposeCause(cz.id, d); toast.success("Cause disposed"); load(); }
    catch (e) { err(e, "Failed"); }
  };
  return (
    <Panel title="causes of action — what this case is asking for" testId="tab-causes">
      {can(access, "filing.accept", "case.create") && (
        <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-slate-100">
          <input className={cls(inputCls, "w-64")} list="cause-suggestions" value={form.cause_type}
            onChange={(e) => setForm({ ...form, cause_type: e.target.value })} placeholder="Cause type…" data-testid="cause-type-input" />
          <datalist id="cause-suggestions">{CAUSE_SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>
          <select className={cls(selectCls, "w-52")} value={form.filed_by_party_id}
            onChange={(e) => setForm({ ...form, filed_by_party_id: e.target.value })} data-testid="cause-party">
            <option value="">Filed by…</option>
            {(ws.parties || []).map((p) => <option key={p.id} value={p.id}>{p.display_name}</option>)}
          </select>
          <input className={cls(inputCls, "flex-1 min-w-[180px]")} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (optional)" data-testid="cause-desc" />
          <button className={btnPrimary} onClick={add} data-testid="cause-add-btn">Add Cause</button>
        </div>
      )}
      <table className="w-full border-collapse">
        <thead><tr><th className={thCls}>Cause</th><th className={thCls}>Filed By</th><th className={thCls}>Filed</th><th className={thCls}>Description</th><th className={thCls}>Status</th><th className={thCls}>Disposition</th><th className={thCls}></th></tr></thead>
        <tbody>
          {causes.map((cz) => (
            <tr key={cz.id} className="hover:bg-slate-50" data-testid={`cause-row-${cz.id}`}>
              <td className={cls(tdCls, "font-semibold")}>{cz.cause_type}</td>
              <td className={tdCls}>{cz.filed_by || "—"}</td>
              <td className={cls(tdCls, "font-mono text-xs")}>{cz.filed_date}</td>
              <td className={cls(tdCls, "text-xs")}>{cz.description || "—"}</td>
              <td className={tdCls}><Badge value={cz.status} /></td>
              <td className={cls(tdCls, "text-xs")}>{cz.disposition || "—"}</td>
              <td className={tdCls}>{cz.status === "ACTIVE" && can(access, "order.sign") &&
                <button className={cls(btnGhost, "px-2 py-0.5 text-xs")} onClick={() => dispose(cz)} data-testid={`cause-dispose-${cz.id}`}>Dispose</button>}</td>
            </tr>
          ))}
          {!causes.length && <tr><td colSpan={7} className={tdCls}>No causes of action recorded. The complaint and any counterclaims belong here.</td></tr>}
        </tbody>
      </table>
    </Panel>
  );
}

// ---------------- NOTES (Odyssey Notes tab) ----------------
export function NotesTab({ caseId }) {
  const [notes, setNotes] = useState(null);
  const [text, setText] = useState("");
  const load = useCallback(() => {
    api.get(`/ujms/cases/${caseId}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([]));
  }, [caseId]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!text.trim()) return;
    try {
      await api.post(`/ujms/cases/${caseId}/notes`, { note: text.trim() });
      toast.success("Note added");
      setText("");
      load();
    } catch (e) { err(e, "Failed to add note"); }
  };
  if (!notes) return <div className="text-sm text-slate-400 p-4">Loading notes…</div>;
  return (
    <Panel title="case notes — internal free-text notes (never docketed, never public)" testId="tab-notes">
      <div className="p-3 border-b border-slate-100 flex gap-2">
        <textarea rows={2} className={cls(inputCls, "flex-1")} value={text} onChange={(e) => setText(e.target.value)}
          placeholder="Type a quick case note…" data-testid="notes-input" />
        <button className={cls(btnPrimary, "self-end")} onClick={add} data-testid="notes-add-btn">Add Note</button>
      </div>
      <div className="divide-y divide-slate-100">
        {notes.map((n) => (
          <div key={n.id} className="px-4 py-2.5" data-testid={`note-row-${n.id}`}>
            <div className="text-[13px] text-slate-800 whitespace-pre-wrap">{n.note}</div>
            <div className="text-[11px] text-slate-400 mt-1">{n.created_by}{n.created_by_role ? ` (${n.created_by_role})` : ""} · {fmtDT(n.created_at)}</div>
          </div>
        ))}
        {!notes.length && <div className="px-4 py-6 text-sm text-slate-400">No notes on this case yet.</div>}
      </div>
    </Panel>
  );
}

// ---------------- DISPOSITION ----------------
export function DispositionTab({ caseId, access, onChanged }) {
  const [d, setD] = useState(null);
  const load = useCallback(() => ujmsCaseDispositions(caseId).then((r) => setD(r.data)).catch(() => {}), [caseId]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="text-sm text-slate-400 p-4">Loading dispositions…</div>;
  return (
    <div className="space-y-4" data-testid="tab-disposition">
      <Panel title="charge-level dispositions — every count carries its own outcome, sentence and fine" testId="disp-sheet-panel">
        <div className="p-1.5"><DispositionSheet caseId={caseId} access={access} onChanged={() => { load(); onChanged && onChanged(); }} /></div>
      </Panel>
      <Panel title="case disposition" testId="disp-case">
        {d.case_disposition ? (
          <div className="text-sm" data-testid="disp-case-record">
            <span className="font-bold text-slate-900">{d.case_disposition.label}</span>
            <span className="text-slate-500 ml-2">{d.case_disposition.method_label} · {d.case_disposition.date}</span>
            {d.case_disposition.notes && <div className="text-xs text-slate-500 mt-1">{d.case_disposition.notes}</div>}
          </div>
        ) : <p className="text-sm text-slate-400">This case has not been disposed.</p>}
      </Panel>
      <Panel title="hearing outcomes" testId="disp-hearings">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>When</th><th className={thCls}>Hearing</th><th className={thCls}>Status</th><th className={thCls}>Result / Outcome</th></tr></thead>
          <tbody>
            {d.hearing_outcomes.map((h) => (
              <tr key={h.id} className="hover:bg-slate-50">
                <td className={cls(tdCls, "font-mono text-xs")}>{fmtDT(h.scheduled_start)}</td>
                <td className={tdCls}>{h.hearing_type_label || h.hearing_type}</td>
                <td className={tdCls}><Badge value={h.status} /></td>
                <td className={cls(tdCls, "text-xs")}>{h.result || h.outcome || h.continuance_reason || "—"}</td>
              </tr>
            ))}
            {!d.hearing_outcomes.length && <tr><td colSpan={4} className={tdCls}>No concluded hearings yet.</td></tr>}
          </tbody>
        </table>
      </Panel>
      {d.charge_dispositions.length > 0 && (
        <Panel title="charge dispositions" testId="disp-charges">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Charge</th><th className={thCls}>Statute</th><th className={thCls}>Status</th></tr></thead>
            <tbody>
              {d.charge_dispositions.map((ch) => (
                <tr key={ch.id} className="hover:bg-slate-50">
                  <td className={tdCls}>{ch.description}</td>
                  <td className={cls(tdCls, "font-mono text-xs")}>{ch.statute_code}</td>
                  <td className={tdCls}><Badge value={ch.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
      {d.cause_dispositions.length > 0 && (
        <Panel title="cause dispositions" testId="disp-causes">
          {d.cause_dispositions.map((cz) => (
            <div key={cz.id} className="text-sm py-1 border-b border-slate-100 last:border-0">
              <b>{cz.cause_type}</b> — {cz.disposition} <span className="text-xs text-slate-400">({fmtD(cz.disposed_at)})</span>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}
